/*───────────────────────────────────────────────────────────────
 📄 File: components/ui/DataGridToolbar.tsx
 🧩 Description:
    Fixed top toolbar for DataGridSimple: title, search input
    with debounce indicator, items-per-page selector, columns
    combobox, custom filters/actions, and add button.
    Extracted from DataGridSimple.tsx.
────────────────────────────────────────────────────────────────*/

import { Plus, Search, X } from 'lucide-react';
import Combobox from './Combobox';

interface Column { key: string; label: string; visible: boolean; }

interface Translations {
    items?: string; columns?: string; allSelected?: string; selected?: string;
    searchOptions?: string; noOptionsFound?: string; selectAll?: string; clearAll?: string;
}

interface Props {
    title: string;
    titleIcon?: React.ReactNode;
    columns: Column[];
    onColumnToggle: (key: string) => void;
    pagination: { limit: number };
    onItemsPerPageChange: (v: number) => void;
    localSearchInput: string;
    searchPlaceholder: string;
    isSearching: boolean;
    onSearchInputChange: (v: string) => void;
    onClearSearch: () => void;
    filters?: React.ReactNode;
    actions?: React.ReactNode;
    onAdd?: () => void;
    addButtonText: string;
    translations?: Translations;
    disableFixedHeader: boolean;
    effectiveSidebarWidth: number;
}

const ITEMS_PER_PAGE = [10, 25, 50, 75, 100, 150, 200];

export function DataGridToolbar({
    title, titleIcon, columns, onColumnToggle, pagination, onItemsPerPageChange,
    localSearchInput, searchPlaceholder, isSearching, onSearchInputChange, onClearSearch,
    filters, actions, onAdd, addButtonText, translations, disableFixedHeader, effectiveSidebarWidth,
}: Props) {
    return (
        <>
            <div
                className={`bg-card/75 backdrop-blur-md border border-border/50 rounded-lg p-3 ${disableFixedHeader ? '' : 'fixed top-[80px] z-40 shadow-lg'}`}
                style={disableFixedHeader ? {} : { left: `${effectiveSidebarWidth + 1}px`, right: '1px' }}
            >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {/* Title */}
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            {titleIcon && <div className="flex items-center justify-center">{titleIcon}</div>}
                            <h1 className="text-xl font-semibold text-foreground">{title}</h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {filters}

                        {/* Search */}
                        <div className="relative bg-blue-50 dark:bg-blue-900/20 p-1 rounded-lg">
                            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-all ${isSearching ? 'text-blue-600 dark:text-blue-400 animate-pulse' : 'text-blue-600 dark:text-blue-400'}`} />
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={localSearchInput || ''}
                                onChange={(e) => onSearchInputChange(e.target.value)}
                                className="w-full sm:w-64 pl-10 pr-10 py-2 text-sm border border-blue-300 dark:border-blue-700 rounded-md bg-white dark:bg-gray-800 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                style={{ minWidth: '200px' }}
                            />
                            {localSearchInput && (
                                <button onClick={onClearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors" title="Clear search">
                                    <X className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                                </button>
                            )}
                            {isSearching && localSearchInput && (
                                <div className="absolute right-10 top-1/2 -translate-y-1/2">
                                    <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                                </div>
                            )}
                        </div>

                        {/* Items per page */}
                        <div className="flex items-center gap-1">
                            <label className="text-xs text-muted-foreground">{translations?.items || 'Items:'}</label>
                            <select value={pagination.limit} onChange={(e) => onItemsPerPageChange(Number(e.target.value))} className="px-2 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                                {ITEMS_PER_PAGE.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </div>

                        {/* Columns */}
                        <div className="w-40">
                            <Combobox
                                options={(columns || []).map((c) => ({ value: c.key, label: c.label, checked: c.visible }))}
                                onToggle={onColumnToggle}
                                placeholder={translations?.columns || 'Columns...'}
                                translations={{ allSelected: translations?.allSelected, selected: translations?.selected, searchOptions: translations?.searchOptions, noOptionsFound: translations?.noOptionsFound, selectAll: translations?.selectAll, clearAll: translations?.clearAll }}
                            />
                        </div>

                        {actions}

                        {onAdd && (
                            <button onClick={onAdd} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {addButtonText}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/* Spacer */}
            {!disableFixedHeader && <div className="h-[60px] sm:h-[10px]" />}
        </>
    );
}
